import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { AlertTriangle, BookMarked, ClipboardCheck, ArrowRight } from 'lucide-react';
import { RISK_BAND_META, MANUAL_STATUS_META } from '../../lib/practiceComplianceAPI';
import { InsuranceSummaryCard } from './PMInsuranceTab';
import { CPDComplianceCard } from '../cpd/CPDComplianceCard';

export const PMOverviewTab = ({ overview, onNavigate }) => {
  const navigate = useNavigate();
  if (!overview) return <div className="animate-pulse h-48 bg-muted rounded-lg mt-4" />;

  return (
    <div className="space-y-4 mt-4">
      {overview.can_manage && <InsuranceSummaryCard onOpen={() => onNavigate('insurance')} />}
      <CPDComplianceCard />
      {!overview.profile_set && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="py-4 flex items-center justify-between gap-3">
            <p className="text-sm text-amber-800">
              <strong>Set up your Practice Profile first</strong> — manuals are generated from your business structure, services, staffing and software.
            </p>
            {overview.can_manage && (
              <Button size="sm" onClick={() => onNavigate('profile')} data-testid="pm-setup-profile-btn">
                Set up profile <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {overview.my_pending_acknowledgements?.length > 0 && (
        <Card className="border-blue-300 bg-blue-50">
          <CardContent className="py-4">
            <p className="text-sm text-blue-800 font-medium mb-2">You have {overview.my_pending_acknowledgements.length} manual(s) awaiting your acknowledgement:</p>
            <div className="flex flex-wrap gap-2">
              {overview.my_pending_acknowledgements.map((m) => (
                <Button key={m.manual_id} size="sm" variant="outline" className="border-blue-300 text-blue-800"
                  onClick={() => navigate(`/practice-manuals/${m.manual_id}`)} data-testid={`pm-pending-ack-${m.manual_id}`}>
                  {m.code} v{m.version}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-muted-foreground"><BookMarked className="w-4 h-4" /> Manuals</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {overview.manuals.length === 0 ? (
              <p className="text-sm text-muted-foreground">No manuals generated yet.</p>
            ) : overview.manuals.map((m) => (
              <div key={m.manual_id} className="flex items-center justify-between text-sm cursor-pointer hover:text-primary"
                onClick={() => navigate(`/practice-manuals/${m.manual_id}`)}>
                <span className="font-medium">{m.code}</span>
                <span className="flex items-center gap-1">
                  {m.effective_version && <Badge className={MANUAL_STATUS_META.approved_effective.color}>v{m.effective_version}</Badge>}
                  {m.working_status && <Badge className={(MANUAL_STATUS_META[m.working_status] || {}).color}>{(MANUAL_STATUS_META[m.working_status] || {}).label} v{m.working_version}</Badge>}
                  {!m.effective_version && !m.working_status && <Badge variant="outline">—</Badge>}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-muted-foreground"><AlertTriangle className="w-4 h-4" /> Residual risk profile</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(overview.risk_bands || {}).map(([band, n]) => (
                <Badge key={band} className={(RISK_BAND_META[band] || {}).color} data-testid={`pm-band-${band}`}>
                  {(RISK_BAND_META[band] || {}).label}: {n}
                </Badge>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-3">{overview.risk_count} risks on the register{overview.risks_unreviewed > 0 ? ` · ${overview.risks_unreviewed} not yet reviewed by the firm` : ''}</p>
            <Button size="sm" variant="ghost" className="mt-2 -ml-2" onClick={() => onNavigate('risks')}>Open risk register <ArrowRight className="w-4 h-4 ml-1" /></Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-muted-foreground"><ClipboardCheck className="w-4 h-4" /> Acknowledgements</CardTitle></CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-foreground">{overview.total_pending_acknowledgements}</p>
            <p className="text-xs text-muted-foreground">outstanding across all effective manuals</p>
            {overview.can_manage && (
              <Button size="sm" variant="ghost" className="mt-2 -ml-2" onClick={() => onNavigate('acks')}>View matrix <ArrowRight className="w-4 h-4 ml-1" /></Button>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Review schedule</CardTitle></CardHeader>
        <CardContent>
          {overview.manuals.filter((m) => m.next_review_date).length === 0 ? (
            <p className="text-sm text-muted-foreground">No approved manuals yet — review dates are set at approval.</p>
          ) : (
            <div className="space-y-1 text-sm">
              {overview.manuals.filter((m) => m.next_review_date).map((m) => (
                <div key={m.manual_id} className="flex justify-between">
                  <span>{m.name}</span>
                  <span className="text-muted-foreground">next review {m.next_review_date} · acknowledged {m.acknowledgements}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
